import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { SessionRecordingData } from '@/types';

export function useSessionRecording(sessionId: string | null, enabled: boolean) {
  const [data, setData] = useState<SessionRecordingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !sessionId) {
      setData(null);
      return;
    }

    let cancelled = false;

    const fetchRecording = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.get<SessionRecordingData>(
          `/admin/sessions/${sessionId}/recording`
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load recording');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRecording();

    return () => {
      cancelled = true;
    };
  }, [sessionId, enabled]);

  return { data, loading, error };
}

export function useRemoteText(url: string | null) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!url) {
      setText(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(url)
      .then((r) => r.text())
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch(() => {
        if (!cancelled) setText(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { text, loading };
}
